import { Fragment, ReactNode, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiOutlineArrowLeft, HiOutlineTrash } from "react-icons/hi2";
import { DEFAULT_RIBBON_STYLE, HomeTileConfigStatus, IRibbonColors } from "@/api_framework/api_modals/HomeTileConfig";

export const StatusBadge = ({ status, title }: { status: HomeTileConfigStatus; title?: string }) => {
  const isActive = status === "ACTIVE";
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
        isActive ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-400"}`} />
      {isActive ? "Active" : "InActive"}
    </span>
  );
};

export const RibbonPreview = ({
  text,
  style = DEFAULT_RIBBON_STYLE,
  isActive = true,
  isFullWidth = false,
  size = "sm",
}: {
  text: string;
  style?: IRibbonColors;
  isActive?: boolean;
  isFullWidth?: boolean;
  size?: "sm" | "lg";
}) => (
  <span
    style={
      isActive
        ? { backgroundColor: style.bg_color, color: style.text_color, borderColor: style.border_color }
        : undefined
    }
    className={`max-w-full items-center truncate border tracking-tight ${
      size === "lg" ? "px-4 py-1.5 text-base font-bold shadow-md" : "px-3 py-1 text-[11px] font-semibold shadow-sm"
    } ${
      isFullWidth ? "flex w-full justify-center rounded-md" : "inline-flex rounded-full"
    } ${isActive ? "" : "border-slate-200 bg-slate-200 text-slate-500"}`}
  >
    {text}
  </span>
);

export const TileAvatar = ({
  name,
  imageUrl,
  className = "",
}: {
  name: string;
  imageUrl?: string;
  className?: string;
}) => {
  const [hasImageFailed, setHasImageFailed] = useState<boolean>(false);

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

  if (imageUrl && !hasImageFailed) {
    return (
      <img
        src={imageUrl}
        alt=""
        loading="lazy"
        onError={() => setHasImageFailed(true)}
        className={`inline-block h-12 w-12 shrink-0 rounded-lg bg-[#EEF5FF] object-contain p-2 ${className}`}
      />
    );
  }

  return (
    <span
      className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#EEF5FF] text-sm font-bold text-[#193A6A] ${className}`}
    >
      {initials || "?"}
    </span>
  );
};

const PERSONA_OPERATOR_SET = new Set(["AND", "OR", "NOT", "XOR"]);

export const PersonaExpressionText = ({ value, className = "" }: { value: string; className?: string }) => (
  <span className={`font-mono text-[11px] leading-5 text-slate-700 ${className}`}>
    {value.split(/(\s+)/).map((part, index) =>
      PERSONA_OPERATOR_SET.has(part.toUpperCase()) ? (
        <span key={`${part}-${index}`} className="font-semibold text-[#2B5DA8]">
          {part}
        </span>
      ) : (
        <span key={`${part}-${index}`}>{part}</span>
      ),
    )}
  </span>
);

const CONFIRM_TONE_CLASSNAME = {
  danger: { icon: "bg-rose-50 text-rose-600", button: "bg-rose-600 hover:bg-rose-700" },
  primary: { icon: "bg-[#EEF5FF] text-[#193A6A]", button: "bg-[#193A6A] hover:bg-[#132c52]" },
};

export const ConfirmModal = ({
  isOpen,
  title,
  description,
  icon = <HiOutlineTrash className="h-5 w-5" />,
  tone = "danger",
  confirmLabel = "Delete",
  loadingLabel = "Deleting…",
  isLoading = false,
  isConfirmDisabled = false,
  onConfirm,
  onClose,
}: {
  isOpen: boolean;
  title: string;
  description: ReactNode;
  icon?: ReactNode;
  tone?: keyof typeof CONFIRM_TONE_CLASSNAME;
  confirmLabel?: string;
  loadingLabel?: string;
  isLoading?: boolean;
  isConfirmDisabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const handleClose = () => {
    if (!isLoading) onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
                <div className="flex items-start gap-3">
                  <span
                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${CONFIRM_TONE_CLASSNAME[tone].icon}`}
                  >
                    {icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <Dialog.Title as="h3" className="text-sm font-semibold text-gray-900">
                      {title}
                    </Dialog.Title>
                    <div className="mt-1 text-xs leading-5 text-slate-500">{description}</div>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3">
                  <button
                    ref={cancelButtonRef}
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isLoading || isConfirmDisabled}
                    className={`rounded-lg px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${CONFIRM_TONE_CLASSNAME[tone].button}`}
                  >
                    {isLoading ? loadingLabel : confirmLabel}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export const BackLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="mb-3 flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-slate-800"
  >
    <HiOutlineArrowLeft className="h-3.5 w-3.5" />
    {label}
  </button>
);
